import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Session, User } from "@supabase/supabase-js";
import {
  signInAcrossProviders,
  signOutEverywhere,
  subscribeToActiveSession,
} from "@/integrations/supabase/authProviders";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const prevUserId = useRef<string | null>(null);

  useEffect(() => {
    // Only update state when the user identity actually changes — prevents
    // the redundant re-render that fires right after getSession() resolves
    // and caused a second "loading" pass downstream.
    const apply = (s: Session | null) => {
      const nextUserId = s?.user?.id ?? null;
      // When the signed-in identity changes (login / logout / switch operator),
      // drop the previous user's cached query data. The dashboard is org-scoped
      // by the operator's Supabase token (operator_memberships), and the backend
      // already 403s cross-org — but without this, React Query would briefly show
      // the prior operator's cached rows to the next one before refetching.
      if (prevUserId.current !== nextUserId) {
        prevUserId.current = nextUserId;
        queryClient.clear();
      }
      setSession((prev) => (prev?.access_token === s?.access_token ? prev : s));
      setUser((prev) => (prev?.id === nextUserId ? prev : s?.user ?? null));
    };

    // ⚠️ ONE subscription across BOTH auth providers, resolved to a single
    // active session. Subscribing per client and applying the event payload
    // would let whichever client does NOT hold the account fire
    // INITIAL_SESSION(null) and clobber the other's live session a beat after
    // sign-in. subscribeToActiveSession re-resolves instead, and fires
    // immediately, so it covers the initial read too.
    const stop = subscribeToActiveSession((s) => {
      apply(s);
      setLoading(false);
    });

    return stop;
  }, [queryClient]);

  const signIn = async (email: string, password: string) => {
    // Primary first, then legacy. The org subdomain is not known yet — it comes
    // from /operator/me after sign-in — so there is nothing to route on.
    return signInAcrossProviders(email, password);
  };

  const signOut = async () => {
    // Both clients: a leftover cookie on .digavouch.com would resurrect a
    // session the operator believes they ended.
    await signOutEverywhere();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// Safe variant for components that may render in demo mode (no AuthProvider).
export function useOptionalAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  return ctx ?? {
    user: null,
    session: null,
    loading: false,
    signIn: async () => ({ error: null }),
    signOut: async () => {},
  };
}
